/**
 * NeueAufgabeDialog — the "Neue Aufgabe" manual task-create modal (STEP 3).
 *
 * A presentational + stateful dialog: the parent owns the network (it injects
 * `onCreate`, the useCreateTask mutation — live POST /tasks or a mock no-op). The
 * form collects title (required), an optional Kategorie/Prozess, Fälligkeit, and
 * a direct Zuständig assignee; on submit it maps to the POST /tasks body (tenant +
 * actor are set server-side — never here). Zero-network by construction, so the
 * dialog is deterministically testable.
 *
 * [Source: apps/web/src/features/communication-v2/components/VnMessageComposeDialog.tsx (modal shell)]
 * [Source: apps/api/src/modules/tasks/dto/create-task.dto.ts (POST /tasks body)]
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, ListPlus, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import type { CreateTaskInput } from '../../../lib/api-client';
import type { AdminMemberDto, TaskProcess } from '@brokly/shared';
import { TASK_PROCESS_VALUES } from '@brokly/shared';
import { processLabel } from '../api/aufgaben-api';

interface NeueAufgabeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tenant members for the Zuständig picker (GET /admin/members). */
  members: AdminMemberDto[];
  /** Create the task (parent wires useCreateTask). Resolves on success. */
  onCreate: (payload: CreateTaskInput) => Promise<unknown>;
  /** Fired once the task is created (parent invalidates + toasts). */
  onCreated?: () => void;
}

type SubmitState = 'idle' | 'saving' | 'failed';

export function NeueAufgabeDialog({
  open,
  onOpenChange,
  members,
  onCreate,
  onCreated,
}: NeueAufgabeDialogProps) {
  const [title, setTitle] = useState('');
  const [process, setProcess] = useState<TaskProcess | ''>('');
  const [dueAt, setDueAt] = useState('');
  const [assignee, setAssignee] = useState('');
  const [state, setState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Reset on open (mirrors the compose-dialog seed effect).
  useEffect(() => {
    if (open) {
      setTitle('');
      setProcess('');
      setDueAt('');
      setAssignee('');
      setState('idle');
      setErrorMsg(null);
    }
  }, [open]);

  // Close on Esc (unless mid-save).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && state !== 'saving') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange, state]);

  const canSave = title.trim().length > 0 && state !== 'saving';

  async function handleSubmit() {
    if (title.trim().length === 0) return;
    const payload: CreateTaskInput = {
      title: title.trim(),
      ...(process ? { process } : {}),
      // The <input type="date"> yields YYYY-MM-DD; widen to an ISO instant so it
      // satisfies the api's @IsISO8601 (end-of-day, local-agnostic UTC).
      ...(dueAt ? { due_at: new Date(`${dueAt}T00:00:00.000Z`).toISOString() } : {}),
      ...(assignee ? { assigned_to: assignee } : {}),
    };
    setState('saving');
    setErrorMsg(null);
    try {
      await onCreate(payload);
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Aufgabe konnte nicht erstellt werden.');
      setState('failed');
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-xl border bg-card shadow-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b p-4">
          <div className="flex flex-col gap-0.5 pr-4">
            <h2 className="flex items-center gap-2 font-semibold text-foreground">
              <ListPlus className="size-4 text-primary" />
              Neue Aufgabe
            </h2>
            <p className="text-xs text-muted-foreground">
              Eine manuell erstellte Aufgabe für die Bearbeitung im Team.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Schliessen"
            className="size-7 shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>

        <form
          id="neue-aufgabe-form"
          className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          {/* Titel (required) */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="neue-aufgabe-title" className="text-xs text-muted-foreground">
              Titel <span className="text-sem-alert">*</span>
            </label>
            <input
              id="neue-aufgabe-title"
              autoFocus
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Was ist zu tun?"
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {/* Kategorie / Prozess (optional) */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="neue-aufgabe-process" className="text-xs text-muted-foreground">
              Kategorie
            </label>
            <select
              id="neue-aufgabe-process"
              value={process}
              onChange={(e) => setProcess(e.target.value as TaskProcess | '')}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Sonstiges (Standard)</option>
              {TASK_PROCESS_VALUES.map((p) => (
                <option key={p} value={p}>
                  {processLabel(p)}
                </option>
              ))}
            </select>
          </div>

          {/* Fälligkeit (optional) */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="neue-aufgabe-due" className="text-xs text-muted-foreground">
              Fälligkeit
            </label>
            <input
              id="neue-aufgabe-due"
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {/* Zuständig (optional) */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="neue-aufgabe-assignee" className="text-xs text-muted-foreground">
              Zuständig
            </label>
            <select
              id="neue-aufgabe-assignee"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Nicht zugewiesen</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.display_name ?? m.email}
                </option>
              ))}
            </select>
          </div>

          {state === 'failed' && errorMsg && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-sem-alert/40 bg-sem-alert-bg px-3 py-2 text-sm text-sem-alert"
            >
              <AlertTriangle className="size-4 shrink-0" />
              {errorMsg}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t p-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={state === 'saving'}>
            Abbrechen
          </Button>
          <Button type="submit" form="neue-aufgabe-form" disabled={!canSave}>
            <ListPlus className="size-4" />
            {state === 'saving' ? 'Erstellen …' : 'Aufgabe erstellen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
